import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseAppSyncService } from '../base-app-sync.service';
import { queryForCommercialFacilities } from 'src/app/graphql/commercial-services/commercial-facilities/query-for-commercial-facilities';
import { getCommercialFacility } from 'src/app/graphql/commercial-services/commercial-facilities/get-commercial-facility';
import { getCommercialFacilityHistories } from 'src/app/graphql/commercial-services/commercial-facilities/get-commercial-facility-histories';
import { createCommercialFacility } from 'src/app/graphql/commercial-services/commercial-facilities/create-commercial-facility';
import { updateCommercialFacility } from 'src/app/graphql/commercial-services/commercial-facilities/update-commercial-facility';
import { deleteCommercialFacility } from 'src/app/graphql/commercial-services/commercial-facilities/delete-commercial-facility';
import {
  getCommercialBalancingTypes,
  getCommercialCompanies,
  getCommercialFacilityTypes,
  getCommercialMarketAreas,
  getCommercialOperatingAreas,
} from 'src/app/graphql/commercial-services/commercial-facilities/get-commercial-facility-lookups';
import { CommercialCompany } from 'src/app/model/commercial-services/commercial-contact';
import {
  CommercialBalancingType,
  CommercialFacility,
  CommercialFacilityConnection,
  CommercialFacilityHistorySummary,
  CommercialFacilitySortOrQuery,
  CommercialFacilityType,
  CommercialMarketArea,
  CommercialOperatingArea,
} from 'src/app/model/commercial-services/commercial-facility';
import { CommercialFacilityUpdateDto } from 'src/app/model/commercial-services/commercial-facility-update-dto';
import { CommercialFacilityBuilderService } from '../builder/commercial-facility-builder.service';

@Injectable({
  providedIn: 'root',
})
export class CommercialFacilityApiService extends BaseAppSyncService {
  private _commercialFacilityBuilder = inject(CommercialFacilityBuilderService);

  getCommercialFacility(
    id: string,
    version?: number
  ): Observable<CommercialFacility | null> {
    return this.query(
      getCommercialFacility,
      { id, version },
      (item: any) =>
        this._commercialFacilityBuilder.buildCommercialFacility(item),
      'getCommercialFacility'
    );
  }

  getCommercialFacilityHistories(
    id: string
  ): Observable<CommercialFacilityHistorySummary[]> {
    return this.query(
      getCommercialFacilityHistories,
      { id },
      (histories: any[]) =>
        histories.map(
          (history): CommercialFacilityHistorySummary => ({
            ...history,
            updatedAt: history.updatedAt
              ? new Date(history.updatedAt)
              : undefined,
          })
        ),
      'getCommercialFacilityHistories'
    );
  }

  queryForCommercialFacilities(
    limit: number,
    page: number,
    query: CommercialFacilitySortOrQuery,
    sort: CommercialFacilitySortOrQuery
  ): Observable<CommercialFacilityConnection> {
    return this.query(
      queryForCommercialFacilities,
      { limit, page, query, sort },
      (item: any) =>
        this._commercialFacilityBuilder.buildCommercialFacilityConnection(item),
      'queryForCommercialFacilities'
    );
  }

  getCommercialOperatingAreas(): Observable<CommercialOperatingArea[]> {
    return this.query(
      getCommercialOperatingAreas,
      {},
      (items: CommercialOperatingArea[]) => items,
      'getCommercialOperatingAreas',
      'cache-first'
    );
  }

  getCommercialMarketAreas(
    operatingAreaId?: string | null
  ): Observable<CommercialMarketArea[]> {
    return this.query(
      getCommercialMarketAreas,
      { operatingAreaId: operatingAreaId ?? null },
      (items: CommercialMarketArea[]) => items,
      'getCommercialMarketAreas',
      'no-cache'
    );
  }

  getCommercialFacilityTypes(): Observable<CommercialFacilityType[]> {
    return this.query(
      getCommercialFacilityTypes,
      {},
      (items: CommercialFacilityType[]) => items,
      'getCommercialFacilityTypes',
      'cache-first'
    );
  }

  getCommercialBalancingTypes(): Observable<CommercialBalancingType[]> {
    return this.query(
      getCommercialBalancingTypes,
      {},
      (items: CommercialBalancingType[]) => items,
      'getCommercialBalancingTypes',
      'cache-first'
    );
  }

  getCommercialCompanies(): Observable<CommercialCompany[]> {
    return this.query(
      getCommercialCompanies,
      {},
      (items: CommercialCompany[]) => items,
      'getCommercialCompanies',
      'cache-first'
    );
  }

  createCommercialFacility(
    input: CommercialFacilityUpdateDto
  ): Observable<CommercialFacility> {
    return this.mutate(
      createCommercialFacility,
      { input },
      (item: any) =>
        this._commercialFacilityBuilder.buildCommercialFacility(item)!,
      'createCommercialFacility'
    );
  }

  updateCommercialFacility(
    id: string,
    input: CommercialFacilityUpdateDto
  ): Observable<CommercialFacility> {
    return this.mutate(
      updateCommercialFacility,
      { id, input },
      (item: any) =>
        this._commercialFacilityBuilder.buildCommercialFacility(item)!,
      'updateCommercialFacility'
    );
  }

  deleteCommercialFacility(id: string): Observable<boolean> {
    return this.mutate(
      deleteCommercialFacility,
      { id },
      (response: { value: boolean }) => response?.value === true,
      'deleteCommercialFacility'
    );
  }
}
